import { Box, Container, Stack, UserIcon } from "@eworkit/iris";
import { omit } from "lodash-es";
import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMedia } from "react-use";
import { useRouter } from "router";

import { Banner, BannerButton } from "@/components/banners";
import { FullScreenRow } from "@/components/Grid";
import { Header } from "@/components/Header";
import { MetaTags } from "@/components/MetaTags";
import { ResponsiveStickyBox } from "@/components/sticky-box/ResponsiveStickyBox";
import { MEDIA_SIZE_SM } from "@/core/media";
import { getBaseURL, IURLSortConfig } from "@/core/url";
import { fetchPublicJobRequestList, IJobRequest, IJobRequestURLFilter, IPageableJobRequest } from "@/data/job-requests";
import { Col, Flex } from "@/eworkitComponents";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { useHasScrolledToElement } from "@/hooks/Viewport";
import { mapToQuery } from "@/infrastructure/Filter/service";
import { SessionContext } from "@/infrastructure/Session";
import { PublicLayout } from "@/views/components/Layout";
import {
  jobRequestFiltersToQueryMapper,
  jobRequestListSearchParamsType,
  JobRequestsFilters,
  JobRequestsFiltersProvider,
  PublicJobRequestsList,
} from "@/views/JobRequests";
import { useLandingPageMetaImgSrc } from "@/views/Landing/utils/useLandingPageMetaImgSrc";
import { router } from "@/views/router";

export const OpenJobsComponent: FC = () => {
  const { t } = useTranslation(["public", "common"]);
  const [, lastJobOfferRef] = useHasScrolledToElement();

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const baseClass = "landing-page";
  const metaImgSrc = useLandingPageMetaImgSrc();

  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: jobRequestListSearchParamsType,
  });

  const fetchData: TPageableFetchFunction<IJobRequest, IPageableJobRequest, IJobRequestURLFilter, IURLSortConfig[]> =
    useCallback((params, options) => {
      const { skillRoleCategories, ...restFilters } = params.filtersConfig ?? {};

      return fetchPublicJobRequestList(
        {
          ...omit(params, "filtersConfig"),
          // TODO: mapToQuery has to be rewritten because of weak type support.
          ...mapToQuery(jobRequestFiltersToQueryMapper, {
            ...restFilters,
            skillRoleCategoryIcon: skillRoleCategories,
          } as any),
        },
        options,
      );
    }, []);

  const fetcherState = useMetaPageableFetcher(fetchData, { meta, changeMeta }, [meta, changeMeta]);

  if (!fetcherState.payload) {
    return null;
  }

  return (
    <>
      <MetaTags
        title={t("META.FIND_JOBS.TITLE")}
        description={t("META.FIND_JOBS.DESCRIPTION")}
        ogImage={`${getBaseURL()}${metaImgSrc}`}
        ogImageAlt={t("META.FIND_JOBS.IMAGE_DESCRIPTION")}
      />

      <div className={baseClass}>
        <JobRequestsFiltersProvider>
          <Stack>
            <ResponsiveStickyBox offsetTop={73}>
              <Box backgroundColor="n25" py={["sm", null, null, "lg"]} px="sm" borderBottom="primary">
                <Container>
                  <Box px={[null, null, null, "sm"]}>
                    <JobRequestsFilters publicMode />
                  </Box>
                </Container>
              </Box>
            </ResponsiveStickyBox>

            <Stack>
              <Box pt={["sm", null, null, "lg"]} pb="xs" px="sm">
                <Container>
                  <Box px={[null, null, null, "sm"]}>
                    <Banner
                      title={t("LANDING_PAGE.JOB_REQUEST.LOGIN_BANNER.TITLE")}
                      description={t("LANDING_PAGE.JOB_REQUEST.LOGIN_BANNER.DESCRIPTION")}
                      icon={<UserIcon />}
                      button={
                        <BannerButton as={Link} to={router.get("login").path}>
                          {t("LANDING_PAGE.BUTTON_LOGIN")}
                        </BannerButton>
                      }
                    />
                  </Box>
                </Container>
              </Box>
            </Stack>

            <Box py={["sm", null, null, "md"]} px="sm">
              <Container>
                <Box px={[null, null, null, "sm"]}>
                  <FullScreenRow>
                    <Col width={12} margin={["sm", false, false, false]}>
                      <Flex direction="column" padding={isSmallScreen ? [false, "md"] : undefined}>
                        <Header color="black" weight="bold" size="md">
                          {t("LANDING_PAGE.JOB_REQUEST.LIST.OPEN_JOBS", { value: fetcherState.payload?.totalElements })}
                        </Header>
                      </Flex>
                    </Col>
                    <Col width={12}>
                      <Flex direction="column" innerBorder={false} padding={isSmallScreen ? [false, "xs"] : undefined}>
                        <PublicJobRequestsList
                          {...fetcherState}
                          sortConfig={meta.sortConfig}
                          lastJobOfferRef={lastJobOfferRef}
                        />
                      </Flex>
                    </Col>
                  </FullScreenRow>
                </Box>
              </Container>
            </Box>
          </Stack>
        </JobRequestsFiltersProvider>
      </div>
    </>
  );
};

export const OpenJobs: FC = memo(() => {
  const { authenticated } = useContext(SessionContext);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const { push } = useRouter();

  if (authenticated) {
    push(router.get("job-requests").path);
    return null;
  }

  const baseClass = "open-jobs";

  return (
    <div className={baseClass}>
      <PublicLayout hideFooter={false} hideTopbar={false} borderBottomTopbar={false}>
        <OpenJobsComponent />
      </PublicLayout>
      {isSmallScreen && <div className={`${baseClass}__filters-padding`} />}
    </div>
  );
});

export default OpenJobs;
